import { useMemo } from 'react';
import {
  LocationFeatureResponse,
  LocationsFeaturesResponse,
} from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { CustomizationFlagQueries, CustomizationFlagTypes } from '@frontend/api-customization-flags';
import { VOICE_AIR_CUSTOMIZATION_FEATURE_ENUMS } from '@frontend/shared';

type UseVoiceAiOptions = {
  locationIds: string[];
  enabled?: boolean;
};

type UseVoiceAiResult = {
  isLoading: boolean;
  activeLocationIds: string[];
};

const isActiveOrPromote = (state?: CustomizationFlagTypes.CustomizationFlagState) => {
  return (
    !!state &&
    [
      CustomizationFlagTypes.CustomizationFlagState.ACTIVE,
      CustomizationFlagTypes.CustomizationFlagState.PROMOTE,
    ].includes(state)
  );
};

const isVoiceAiFeature = (f: LocationFeatureResponse) =>
  !!f?.featureEnum && VOICE_AIR_CUSTOMIZATION_FEATURE_ENUMS.some((enumVal) => enumVal === f.featureEnum);

const buildLocationFlagMapFromBatched = (
  data: LocationsFeaturesResponse | undefined,
  allowedIds: string[]
): Record<string, LocationFeatureResponse | undefined> => {
  if (!data) return {};

  const byLocation = data.locationsFeatures ?? {};
  const allowedSet = new Set(allowedIds);
  const flagMap: Record<string, LocationFeatureResponse | undefined> = {};

  Object.entries(byLocation).forEach(([locationId, flagsData]) => {
    if (!allowedSet.has(locationId)) return;

    const features = flagsData?.features ?? [];

    // Prefer the first voice AI feature that is active/promote.
    // If the first matching enum (e.g. TRUELARK_AI_VOICE) is inactive, continue to check the next
    // (e.g. WEAVE_VOICE_AIR) so that an active flag is not shadowed by an inactive one.
    const match =
      features.find((f) => isVoiceAiFeature(f) && isActiveOrPromote(f?.state)) ?? features.find(isVoiceAiFeature);

    if (match) {
      flagMap[locationId] = match;
    }
  });

  return flagMap;
};

const getActiveLocationIds = (flagMap: Record<string, LocationFeatureResponse | undefined>): string[] => {
  return Object.entries(flagMap)
    .filter(([, value]) => isActiveOrPromote(value?.state))
    .map(([locationId]) => locationId);
};

export const useVoiceAiLocations = (options: UseVoiceAiOptions): UseVoiceAiResult => {
  const { locationIds, enabled = true } = options;

  const shouldFetchFlags = enabled && locationIds.length > 0;

  const { data, isLoading } = CustomizationFlagQueries.useBatchedMultiCustomizationFlags(locationIds, {
    enabled: shouldFetchFlags,
  });

  const activeLocationIds = useMemo(() => {
    if (!shouldFetchFlags || !data) return [];
    const flagMap = buildLocationFlagMapFromBatched(data, locationIds);
    return getActiveLocationIds(flagMap);
  }, [data, locationIds, shouldFetchFlags]);

  return {
    isLoading,
    activeLocationIds,
  };
};
